/**
 * filterRowsLayout.ts — armado de los renglones de la caja de filtros (lógica pura, testeable).
 *
 * PT-#57 R2-1: DESCUENTO va como ÚLTIMO item de la ÚLTIMA fila dimensional (Samsung =
 * Cat/Subcat/Segmento → Cat/Subcat/Segmento/Descuento), no en una fila suelta.
 *
 * PT-#87 F1: antes se recalculaban SIEMPRE todos los anchos de esa fila a 100/N, ignorando
 * el `widthPct` del filterLayout. Con Adidas (Categoría 25 + Subcategoría 25) la fila
 * quedaba a 33% y Subcategoría no caía debajo de Sitio (fila 1 a 25%). Regla nueva:
 *   - Si los campos que REALMENTE se dibujan en la fila (field enabled + dimensión
 *     habilitada para el cliente; ver FilterBox.renderDimension) suman, junto con
 *     Descuentos al cuarto de grilla (25%), <= 100%, se respetan sus `widthPct` y
 *     Descuentos entra a 25% (Adidas: 25/25/25, alineado a la fila 1).
 *   - Si no entra, o algún campo dibujado no trae `widthPct`, o Descuentos queda SOLO en
 *     la fila (todos los demás ocultos: TIA/LG sin subcategoría/segmento), se mantiene el
 *     recálculo legacy a 100/N (N cuenta todos los fields de la fila). En el caso "solo",
 *     eso deja Descuentos a 50%, alineado a la fila numérica de abajo (Precio/Posición
 *     a 50%); es el comportamiento vigente de TIA y LG y no se cambia.
 */
import { FilterControl, FilterRow, GRID_QUARTER } from '../config/filterBoxConfigs';

/** Ancho de Descuentos cuando comparte fila con dimensionales: cuarto de grilla. */
export const DISCOUNT_WIDTH_PCT = GRID_QUARTER;

// Tolerancia para sumas con decimales (ej. 33.33 * 3).
const EPSILON = 0.01;

/**
 * Agrega `discount` al final de la última fila y resuelve los anchos de esa fila.
 * @param rows   renglones dimensionales (filterLayout per-cliente o default).
 * @param isDrawn true si el field se dibuja (enabled y la dimensión está habilitada).
 */
export function appendDiscountToLastRow(
  rows: FilterRow[],
  isDrawn: (field: FilterControl) => boolean,
): FilterRow[] {
  return rows.map((row, i) => {
    if (i !== rows.length - 1) return row;
    const discount: FilterControl = { key: 'discount', enabled: true, widthPct: DISCOUNT_WIDTH_PCT };
    const drawn = row.fields.filter(isDrawn);
    const drawnHaveWidths = drawn.every(f => typeof f.widthPct === 'number');
    const drawnSum = drawn.reduce((s, f) => s + (f.widthPct || 0), 0);
    const fitsGrid = drawn.length > 0 && drawnHaveWidths && drawnSum + DISCOUNT_WIDTH_PCT <= 100 + EPSILON;
    if (fitsGrid) {
      return { fields: [...row.fields.map(f => ({ ...f })), discount] };
    }
    // Recálculo legacy (PT-#57 R2-1): todos los fields de la fila a 100/N.
    const fields = [...row.fields, discount];
    const w = Math.round((100 / fields.length) * 100) / 100;
    return { fields: fields.map(f => ({ ...f, widthPct: w })) };
  });
}
